import Button from '@components/atoms/button'
import BookFreeClassBtn from '@components/common/book-free-class-btn'
import Link from 'next/link'
import { FastlyImage } from '../common/image'

type Pillar = {
  image: string
  title: string
  copy: string
  color: string
}

const PILLARS: Pillar[] = [
  {
    image: '/images/courses/math/gifs/01-number-sense.gif',
    title: 'Number Sense',
    copy:
      'Counting, place value, number bonds. The foundation every concept sits on.',
    color: 'text-yellow-200',
  },
  {
    image: '/images/courses/math/gifs/02-fractions.gif',
    title: 'Fractions & Decimals',
    copy:
      'Visual teaching using Polypad fraction bars. Concepts before calculations, every time.',
    color: 'text-orange',
  },
  {
    image: '/images/courses/math/gifs/03-algebra.gif',
    title: 'Algebra Foundations',
    copy:
      'Variables, equations, functions, and patterns from pre-algebra through advanced, mapped to grade and board.',
    color: 'text-green-100',
  },
  {
    image: '/images/courses/math/gifs/04-probability.gif',
    title: 'Probability',
    copy:
      'Dice, coins, and real experiments understood through Polypad simulations before any formula appears.',
    color: 'text-yellow-200',
  },
  {
    image: '/images/courses/math/gifs/05-geometry.gif',
    title: 'Geometry',
    copy:
      'Shapes, angles, area, and volume made visual with GeoGebra. Students see the relationship, not just the formula.',
    color: 'text-violet-100',
  },
  {
    image: '/images/courses/math/gifs/06-measurement.gif',
    title: 'Measurement',
    copy:
      "Length, mass, time, and capacity with real-world problems connected to your child's actual board.",
    color: 'text-orange',
  },
  {
    image: '/images/courses/math/gifs/07-data.gif',
    title: 'Data Handling',
    copy:
      'Collecting, organising, and interpreting data - charts, graphs, and real datasets, not textbook exercises.',
    color: 'text-blue-200',
  },
]

type Props = {
  urlQuery?: string
  onViewCurriculum?: () => void
}

const FoundationsFirst = ({ urlQuery, onViewCurriculum }: Props) => {
  return (
    <section className="py-12 lg:py-16 px-5">
      <div className="container mx-auto max-w-6xl">
        <div className="text-center mb-10">
          <h2 className="text-2xl lg:text-4xl font-600 leading-tight mb-4 text-grey max-w-4xl mx-auto">
            Foundations first. Always.
          </h2>
          <p className="text-base lg:text-lg text-grey max-w-4xl mx-auto">
            Every concept your child will ever need sits on one of these
            pillars. Master them early and Math stops being hard. Skip them and
            it never gets easier.
          </p>
        </div>

        <div className="flex flex-wrap justify-center gap-5">
          {PILLARS.map((pillar) => (
            <div
              key={pillar.title}
              className="bg-white rounded-xl border border-grey-300 p-2 lg:p-3 flex flex-col gap-3 w-64"
            >
              <FastlyImage
                src={pillar.image}
                alt={pillar.title}
                width={252}
                height={189}
              />
              <h3
                className={`text-lg lg:text-xl font-700 leading-tight ${pillar.color}`}
              >
                {pillar.title}
              </h3>
              <p className="text-sm lg:text-base text-grey-800 leading-snug">
                {pillar.copy}
              </p>
            </div>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row justify-center items-center gap-4 mt-10">
          <BookFreeClassBtn className="w-full sm:w-auto" query={urlQuery} />
          {onViewCurriculum ? (
            <Button
              variant="outline"
              intent="primary"
              onClick={onViewCurriculum}
              className="w-full sm:w-auto"
            >
              View full curriculum
            </Button>
          ) : (
            <Link href="#curriculum" prefetch={false}>
              <a>
                <Button
                  variant="outline"
                  intent="primary"
                  className="w-full sm:w-auto"
                >
                  View full curriculum
                </Button>
              </a>
            </Link>
          )}
        </div>
      </div>
    </section>
  )
}

export default FoundationsFirst
